import React, {
  EventHandler, ReactElement,
  RefObject, useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from "react";
import {
  Card,
  CardContent,
  Divider,
  Fade,
  Grid,
  MenuItem,
  Stack,
  Typography
} from "@mui/material";

import DefaultFormField from "./default";
import { useFormValues } from "../../../contexts/FormContext";
import CheckboxFormField from "./checkbox";
import { FormChoices, FormField } from "../../../api/types/form";
import SelectionFormField from "./selection";
import ModalFormField from "./modal";
import TextareaFormField from "./textarea";
import TextEditorFormField from "./texteditor";
import { TemplateFormType } from "../../../utils/cms";
import DateTimeFormField from "./datetime";
import DateFormField from "./date";
import PageSelectorFormField from "./page-selector";
import FileUploadFormField from "./file-upload";
import ColorFormField from "./color";
import CollectionFormField from "./collection";
import {
  CopyOutlined,
  DeleteOutlined,
  TranslationOutlined
} from "@ant-design/icons";
import ContextMenu from "../../qlive/ContextMenu";
import { useTranslation } from "../../../contexts/TranslationContext";
import { TranslateDialogRef } from "../../dialog/TranslateDialog";
import IconSelection from "./icon-selection";
import AccordionFormField from "./accordeon";

export type FormFieldInputVars = {
  [key: string]: string;
};

export type FormFieldProps = {
  type: FormField["type"];
  name: string;
  label: string;
  required: boolean;
  value: any;
  placeholder?: string;
  choices?: FormChoices;
  multiple?: boolean;
  onChange: (newValue: any) => void;
  disabled?: boolean;
  onKeyUp?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  formIndex?: number;
  cssClassName?: string;
  inputVars?: FormFieldInputVars;
  children?: Array<FormFieldProps | TemplateFormType>;
  help?: string;
  attr?: any;
  hasTimePublishing?: boolean;
  inCmsEdit?: boolean;
  locale?: string;
  formType?: string;
};

// any react form field component which includes FomFieldProps
export type FormFieldComponent = any;

type FormFieldTypesType = {
  [type: string]: FormFieldComponent;
};

export const FormFieldTypes: FormFieldTypesType = {
  text: DefaultFormField,
  password: DefaultFormField,
  number: DefaultFormField,
  money: DefaultFormField,
  url: DefaultFormField,
  checkbox: CheckboxFormField,
  choice: SelectionFormField,
  color: ColorFormField,
  textarea: TextareaFormField,
  texteditor: TextEditorFormField,
  datetime: DateTimeFormField,
  date: DateFormField,
  page: PageSelectorFormField,
  file: FileUploadFormField,
  modal: ModalFormField,
  accordion: AccordionFormField,
  collection: CollectionFormField,
  "icon-selection": IconSelection
};

export const isFormFieldImplemented = (type: string): boolean => {
  return Boolean(FormFieldTypes[type]);
};

type NewFormProps = {
  formIndex?: number;
  fields: Array<FormField>;
  additionalFields?: Array<FormField>;
  blockLayout: boolean;
  blockNames?: Array<string>;
  blockSizes?: { [name: string]: number };
  onSubmit?: () => void;
  translateDialogRef?: RefObject<TranslateDialogRef | undefined>;
  formContent?: React.ReactNode;
  passedLocale?: string;
  formType?: string;
  nested?: boolean;
};

const translatableContent = ["text", "textarea", "texteditor"];

function FieldComponent({
                          formIndex,
                          fieldConfig,
                          translateDialogRef,
                          locale
                        }: {
  formIndex?: number;
  fieldConfig: FormFieldProps;
  translateDialogRef?: RefObject<TranslateDialogRef | undefined>;
  locale?: string;
}) {
  const { translate } = useTranslation();
  const Component = FormFieldTypes[fieldConfig.type];
  const { value, onChange } = useFormValues(fieldConfig.name);

  return useMemo(
    () => {
        const props = { ...fieldConfig, value: undefined };

        const getContextMenuItems = (handleMenuClose: () => void) => {
            const copyColumnName = () => {
                navigator.clipboard.writeText(fieldConfig.name);
                handleMenuClose();
            };

            const copyValue = () => {
                let text = fieldConfig.value;
                if (typeof text !== "string") {
                    text = JSON.stringify(fieldConfig.value);
                }
                navigator.clipboard.writeText(text);
                handleMenuClose();
            };

            const resetValue = () => {
                onChange(null);
                handleMenuClose();
            };

            const translateValue = () => {
                translateDialogRef?.current?.registerUpdateCallback(onChange);
                translateDialogRef?.current?.setTranslateObj({
                    text: fieldConfig.value,
                    sourceLocale: "de"
                });
                translateDialogRef?.current?.open();
                handleMenuClose();
            };

            const renderTranslateItem = () => {
                if (
                    translateDialogRef &&
                    translatableContent.includes(fieldConfig.type)
                ) {
                    return [
                        <Divider key="divider-0" />,
                        <MenuItem
                            key="translate-value"
                            onClick={translateValue}
                            sx={{ gap: 1 }}>
                            <TranslationOutlined style={{ fontSize: 16 }} />
                            {translate("form.general.field_context.translate_val")}
                        </MenuItem>
                    ];
                }
                return [];
            };

            return [
                <MenuItem
                    key="copy-column-name"
                    onClick={copyColumnName}
                    sx={{ gap: 1 }}>
                    <CopyOutlined style={{ fontSize: 16 }} />
                    {translate("form.general.field_context.col_copy")}
                </MenuItem>,
                <MenuItem key="copy-value" onClick={copyValue} sx={{ gap: 1 }}>
                    <CopyOutlined style={{ fontSize: 16 }} />
                    {translate("form.general.field_context.val_copy")}
                </MenuItem>,
                ...renderTranslateItem(),
                <Divider key="divider-1" />,
                <MenuItem
                    key="reset-value"
                    onClick={resetValue}
                    sx={{
                        gap: 1,
                        color: theme => theme.palette.error.main
                    }}>
                    <DeleteOutlined style={{ fontSize: 16 }} />
                    {translate("form.general.field_context.field_reset")}
                </MenuItem>
            ];
        };

      return (
        <ContextMenu
          key={fieldConfig.name}
          menuItems={getContextMenuItems}>
          <Component
            {...props}
            formIndex={formIndex}
            value={value}
            onChange={onChange}
            cssClassName={props?.attr?.["class"]}
            locale={locale}
          />
        </ContextMenu>
      );
    },
    [Component, fieldConfig, formIndex, locale, translateDialogRef, value, onChange, translate]
  );
}

export default function NewForm({
                                  formIndex,
                                  fields,
                                  blockLayout,
                                  blockNames,
                                  blockSizes,
                                  onSubmit,
                                  additionalFields = [],
                                  translateDialogRef,
                                  formContent,
                                  passedLocale,
                                  nested = false
                                }: NewFormProps) {
  const { translate } = useTranslation();

  const formRef = useRef<HTMLFormElement | HTMLDivElement>();
  const [showForm, setShowForm] = useState(false);

  useLayoutEffect(() => {
    if (fields.length > 0) {
      setShowForm(true);
    }
  }, [fields]);

  const filteredFields = fields.filter(field =>
    isFormFieldImplemented(field.type)
  );
  const filteredAdditionalFields = additionalFields.filter(field =>
    isFormFieldImplemented(field.type)
  );

  const onFormSubmit = (event: SubmitEvent) => {
    event.preventDefault();
    onSubmit && onSubmit();
  };

  const FormComponent = useCallback((children: ReactElement) => {
    const updateFormRef = (ref: HTMLFormElement | HTMLDivElement | null) => {
      if (ref) {
        formRef.current = ref;
      }
    };

    if (nested) {
      return (
        <div ref={updateFormRef} onSubmit={onFormSubmit as EventHandler<any>}>
          {children}
        </div>
      );
    }
    return (
      <form ref={updateFormRef}
            method="post"
            onSubmit={onFormSubmit as EventHandler<any>}>
        {children}
      </form>
    );
  }, [nested]);

  return useMemo(() => {
    const renderField = (fieldConfig: any, fieldIndex: number) => {
      return (
        <FieldComponent
          key={fieldIndex}
          formIndex={formIndex}
          fieldConfig={fieldConfig}
          translateDialogRef={translateDialogRef}
          locale={passedLocale}
        />
      );
    };

    const renderBlockFields = (blockName: string) => {
      const blockItems = filteredFields.filter(
        item => item.attr?.["block-name"] === blockName
      );

      return blockItems.map(renderField);
    };


    if (blockLayout) {
      return (
        <Fade in={showForm}>
          {FormComponent(
            <>
              {formContent && (
                <Stack spacing={2} sx={{ mb: 1 }}>
                  {formContent}
                  <Divider />
                </Stack>
              )}
              <Grid container spacing={2}>
                {blockNames?.map((blockName: string) => (
                  <Grid
                    item
                    key={`block-${blockName}`}
                    id={`block-${blockName}-${formIndex}`}
                    xs={12}
                    md={blockSizes?.[blockName] ?? 12}>
                    <Card
                      elevation={0}
                      sx={{
                        overflow: "visible",
                        height: "100%"
                      }}>
                      <CardContent
                        sx={{
                          height: "100%",
                          position: "relative",
                          "&:last-child": { pb: 2 }
                        }}>
                        <Stack spacing={2}>
                          {renderBlockFields(blockName)}
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </>
          )}
        </Fade>
      );
    }

    return (
      <Fade in={showForm}>
        {FormComponent(
          <>
            <Stack spacing={4}>
              <Stack spacing={2}>
                {formContent && (
                  <Stack spacing={2} sx={{ mb: 1 }}>
                    {formContent}
                    <Divider />
                  </Stack>
                )}
                {filteredFields.map(renderField)}
              </Stack>
              {filteredAdditionalFields.length > 0 && (
                <Stack
                  spacing={2}
                  sx={{
                    borderWidth: 0.2,
                    borderStyle: "solid",
                    p: 2,
                    borderRadius: 1,
                    borderColor: theme => theme.palette.divider
                  }}>
                  <Typography variant="subtitle1">
                    {translate(
                      "pages.cms.pages.additional_fields"
                    )}
                  </Typography>
                  {filteredAdditionalFields.map(renderField)}
                </Stack>
              )}
              <input
                type="submit"
                onSubmit={onFormSubmit as EventHandler<any>}
                style={{ display: "none" }}
              />
            </Stack>
          </>
        )}
      </Fade>
    );
  }, [fields, showForm, FormComponent]);
}
